/**
 * Report Tab Component
 * Project summary and export options
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  FileText, 
  Download, 
  Printer, 
  Mail, 
  Image, 
  AlertTriangle,
  MapPin,
  Loader2,
  Eye,
  Copy,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { type DBProject } from '../../../db';
import { projectsApi } from '../../../lib/api';

interface ReportTabProps {
  project: DBProject;
}

interface Anomaly {
  id: string;
  code: string;
  level: 1 | 2 | 3;
  description?: string;
  photo?: { filename: string };
  element?: { type: string; code: string };
}

interface ExportData {
  data: Record<string, unknown>;
  meta: {
    filename: string;
    anomalyCount: number;
    elementCount: number;
    projectCode: string;
    projectName: string;
  };
}

// Colors for charts
const SEVERITY_COLORS = {
  1: '#22c55e',  // green - Level 1 (Urgent in ENEL spec, but shown as low severity)
  2: '#f59e0b',  // amber
  3: '#ef4444',  // red
};

const SEVERITY_LABELS = {
  1: 'Level 1',
  2: 'Level 2', 
  3: 'Level 3',
};

export function ReportTab({ project }: ReportTabProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [exportData, setExportData] = useState<ExportData | null>(null);
  const [copied, setCopied] = useState(false);

  // Load anomalies from server
  useEffect(() => {
    const loadAnomalies = async () => {
      setIsLoading(true);
      try {
        const response = await projectsApi.getAnomalies(project.id, { limit: 1000 });
        setAnomalies(response.data?.data || []);
      } catch (error) {
        console.error('Failed to load anomalies:', error);
        setAnomalies([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadAnomalies();
  }, [project.id]);

  // Calculate severity distribution
  const severityData = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0 };
    anomalies.forEach(a => {
      if (a.level >= 1 && a.level <= 3) {
        counts[a.level]++;
      }
    });
    return [
      { name: SEVERITY_LABELS[1], value: counts[1], color: SEVERITY_COLORS[1] },
      { name: SEVERITY_LABELS[2], value: counts[2], color: SEVERITY_COLORS[2] },
      { name: SEVERITY_LABELS[3], value: counts[3], color: SEVERITY_COLORS[3] },
    ].filter(d => d.value > 0);
  }, [anomalies]);

  // Calculate anomaly type distribution (top 10)
  const anomalyTypeData = useMemo(() => {
    const counts: Record<string, number> = {};
    anomalies.forEach(a => {
      counts[a.code] = (counts[a.code] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));
  }, [anomalies]);

  const handleExportPDF = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsExporting(false);
    toast.info('PDF export coming soon', {
      description: 'This feature will be available in a future update',
    });
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    try {
      const response = await projectsApi.export(project.id);
      if (response.data?.success) {
        setExportData(response.data as ExportData);
        setShowPreview(true);
      } else {
        toast.error('Export failed', {
          description: response.data?.error || 'Unknown error',
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed', {
        description: 'Could not generate SWIM JSON export',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = () => {
    if (!exportData) return;
    
    const jsonString = JSON.stringify(exportData.data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = exportData.meta.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setShowPreview(false);
    toast.success('Export downloaded', {
      description: exportData.meta.filename,
    });
  };

  const handleCopyJSON = async () => {
    if (!exportData) return;
    await navigator.clipboard.writeText(JSON.stringify(exportData.data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const jsonPreview = useMemo(() => {
    if (!exportData) return '';
    const str = JSON.stringify(exportData.data, null, 2);
    return str.length > 10000 ? str.slice(0, 10000) + '\n\n... (truncated)' : str;
  }, [exportData]);

  return (
    <div className="h-full overflow-auto p-6 bg-slate-100 dark:bg-slate-900 dark-scrollbar">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <FileText className="w-7 h-7 text-blue-500 dark:text-blue-400" />
              Project Report
            </h2>
            <p className="text-slate-500 font-mono mt-1">
              Summary and analysis for project {project.code}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportJSON}
              disabled={isExporting || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600/80 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-mono text-sm border border-blue-500/50"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export SWIM JSON
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600/80 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 font-mono text-sm border border-green-500/50"
            >
              <Printer className="w-4 h-4" />
              Generate PDF
            </button>
          </div>
        </div>

        {/* Project Summary Card */}
        <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6">
          <h3 className="text-lg font-mono font-semibold text-slate-700 dark:text-slate-200 mb-4 uppercase tracking-wider text-sm">
            Inspection Summary
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">Project</p>
              <p className="font-mono font-semibold text-slate-800 dark:text-slate-100">{project.code}</p>
              <p className="text-sm font-mono text-slate-500 dark:text-slate-400">{project.name}</p>
            </div>
            <div>
              <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">Line</p>
              <p className="font-mono font-semibold text-slate-800 dark:text-slate-100">{project.line || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">Inspection Date</p>
              <p className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                {project.surveyDate 
                  ? format(new Date(project.surveyDate), "yyyy.MM.dd")
                  : '-'
                }
              </p>
            </div>
            <div>
              <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">Report Date</p>
              <p className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                {format(new Date(), "yyyy.MM.dd")}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6 text-center">
            <Image className="w-10 h-10 text-blue-500 dark:text-blue-400 mx-auto mb-2" />
            <p className="text-3xl font-mono font-bold text-slate-800 dark:text-slate-100">{project.photoCount}</p>
            <p className="text-slate-500 font-mono text-sm uppercase tracking-wider">Photos</p>
          </div>
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6 text-center">
            <MapPin className="w-10 h-10 text-purple-500 dark:text-purple-400 mx-auto mb-2" />
            <p className="text-3xl font-mono font-bold text-slate-800 dark:text-slate-100">{project.elementCount}</p>
            <p className="text-slate-500 font-mono text-sm uppercase tracking-wider">Elements</p>
          </div>
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 dark:text-amber-400 mx-auto mb-2" />
            <p className="text-3xl font-mono font-bold text-slate-800 dark:text-slate-100">
              {isLoading ? '...' : anomalies.length}
            </p>
            <p className="text-slate-500 font-mono text-sm uppercase tracking-wider">Anomalies</p>
          </div>
        </div>

        {/* Charts */}
        {!isLoading && anomalies.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Severity Pie Chart */}
            <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6">
              <h3 className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-200 mb-4 uppercase tracking-wider">
                Anomalies by Severity
              </h3>
              <div className="h-64">
                {severityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={severityData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {severityData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'var(--tooltip-bg, #1e293b)', 
                          border: '1px solid var(--tooltip-border, #334155)',
                          borderRadius: '8px',
                          fontFamily: 'JetBrains Mono, monospace'
                        }}
                        labelStyle={{ color: 'var(--tooltip-text, #e2e8f0)' }}
                      />
                      <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono, monospace' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 font-mono">
                    No data
                  </div>
                )}
              </div>
            </div>

            {/* Anomaly Type Bar Chart */}
            <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6">
              <h3 className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-200 mb-4 uppercase tracking-wider">
                Top Anomaly Codes
              </h3>
              <div className="h-64">
                {anomalyTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={anomalyTypeData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-stroke, #334155)" />
                      <XAxis type="number" stroke="var(--axis-stroke, #64748b)" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={60} stroke="var(--axis-stroke, #64748b)" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'var(--tooltip-bg, #1e293b)', 
                          border: '1px solid var(--tooltip-border, #334155)',
                          borderRadius: '8px',
                          fontFamily: 'JetBrains Mono, monospace'
                        }}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 font-mono">
                    No data
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* No anomalies message */}
        {!isLoading && anomalies.length === 0 && (
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400 font-mono">No anomalies recorded for this project</p>
            <p className="text-slate-400 dark:text-slate-500 font-mono text-sm mt-1">Mark anomalies in photos to generate statistics</p>
          </div>
        )}

        {/* Export Actions */}
        <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6">
          <h3 className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-200 mb-4 uppercase tracking-wider">
            Actions
          </h3>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExportJSON}
              disabled={isExporting || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors font-mono text-sm border border-slate-300 dark:border-slate-600/50 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export SWIM JSON
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors font-mono text-sm border border-slate-300 dark:border-slate-600/50 disabled:opacity-50"
            >
              <FileText className="w-4 h-4" />
              Generate PDF
            </button>
            <button
              disabled
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 rounded-lg font-mono text-sm border border-slate-300 dark:border-slate-600/50 cursor-not-allowed"
            >
              <Mail className="w-4 h-4" />
              Send Report
            </button>
          </div>
        </div>
      </div>

      {/* Export Preview Modal */}
      {showPreview && exportData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col m-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <h3 className="text-lg font-mono font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  SWIM JSON Export Preview
                </h3>
                <p className="text-sm font-mono text-slate-500 dark:text-slate-400 mt-1">
                  {exportData.meta.filename} • {exportData.meta.anomalyCount} issues
                </p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-blue-600 dark:text-blue-400">
                  {exportData.meta.anomalyCount}
                </p>
                <p className="text-xs font-mono text-slate-500 uppercase">Issues</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-slate-700 dark:text-slate-300">
                  {exportData.meta.elementCount}
                </p>
                <p className="text-xs font-mono text-slate-500 uppercase">Elements</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-slate-700 dark:text-slate-300">
                  {exportData.meta.projectCode}
                </p>
                <p className="text-xs font-mono text-slate-500 uppercase">Project</p>
              </div>
            </div>

            {/* JSON Preview */}
            <div className="flex-1 overflow-auto p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-mono text-slate-500">JSON Preview</span>
                <button
                  onClick={handleCopyJSON}
                  className="flex items-center gap-1 text-sm font-mono text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-auto max-h-[400px] font-mono">
                {jsonPreview}
              </pre>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors font-mono text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-mono text-sm"
              >
                <Download className="w-4 h-4" />
                Download JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
